/**
 * Tracks the selected option's full value for label display in TriggerInput.
 *
 * When the value changes externally (e.g. form reset, programmatic update),
 * calls loadOptions({ mode: 'initialValue' }) to resolve the display label.
 * Also ensures the selected option stays in the options list (optionsWithSelected)
 * so ActionList's optionMap can always resolve it — even after options are
 * cleared on dropdown close.
 */
import type {
  ActionListFullValue,
  ActionListOption,
  ActionListValue,
} from '@latitude/action-list';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { findValueInItems } from 'utils';

import type {
  UseValueResolutionParams,
  UseValueResolutionReturn,
} from './types';

export const useValueResolution = <
  isNullable extends boolean = false,
  T = undefined,
>({
  loadOptions,
  value,
  options,
  setOptions,
  onChange: passedOnChange,
}: UseValueResolutionParams<isNullable, T>): UseValueResolutionReturn<
  isNullable,
  T
> => {
  const [fullValue, setFullValue] = useState<ActionListFullValue<
    false,
    isNullable,
    T
  > | null>(null);

  // Resolve display label when value changes externally
  useEffect(() => {
    if (!value || value.length < 1) {
      setFullValue(null);
      return;
    }

    if (value !== fullValue?.value) {
      let stale = false;

      loadOptions({ mode: 'initialValue', value })
        .then((result) => {
          if (stale) {
            return;
          }

          const foundFullValue = findValueInItems<T>(result.newOptions, value);
          setFullValue(foundFullValue ?? null);

          // Merge into options so ActionList's optionMap can resolve the label.
          setOptions((existing) => {
            const existingValues = new Set(
              existing
                .filter((o) => 'value' in o)
                .map((o) => (o as { value: string }).value),
            );
            const newOpts = result.newOptions.filter(
              (o) =>
                'value' in o &&
                !existingValues.has((o as { value: string }).value),
            );
            if (newOpts.length > 0) {
              return [...existing, ...newOpts];
            }
            return existing;
          });
        })
        .catch(() => {
          // loadOptions rejection — clear fullValue so the display
          // doesn't show a stale label for a value that failed to resolve.
          if (!stale) {
            setFullValue(null);
          }
        });

      return () => {
        stale = true;
      };
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs on value change only
  }, [value]);

  // Wrap onChange to keep fullValue in sync with the selected option
  const handleChange = useCallback(
    (
      val: ActionListValue<false, isNullable>,
      fullVal: ActionListFullValue<false, isNullable, T>,
    ) => {
      passedOnChange?.(val, fullVal);
      setFullValue(fullVal);
    },
    [passedOnChange],
  );

  // Ensure the selected option is always in the options list so ActionList's
  // optionMap can resolve its label for TriggerInput display.
  const optionsWithSelected = useMemo(() => {
    if (!fullValue || !value) {
      return options;
    }
    const alreadyPresent = options.some(
      (o) => 'value' in o && (o as { value: string }).value === value,
    );
    if (alreadyPresent) {
      return options;
    }
    return [...options, fullValue as ActionListOption<T>];
  }, [options, fullValue, value]);

  return {
    handleChange,
    optionsWithSelected,
  };
};
